import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import BrandVideoPlayer from '../training/BrandVideoPlayer';
import { checkCanEditReel } from '../../services/training';
import { resolveReelDisplay } from '../../utils/reelCrop';

export default function ReelCard({
  reel,
  isLiked = false,
  audioUnlocked = false,
  onLike,
  onVisible,
  isLast = false,
  onNearEnd,
}) {
  const navigate = useNavigate();
  const cardRef = useRef(null);
  const videoRef = useRef(null);
  const [isInView, setIsInView] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [userMuted, setUserMuted] = useState(false);
  const [autoplayFallbackMuted, setAutoplayFallbackMuted] = useState(false);
  const muted = !audioUnlocked || userMuted || autoplayFallbackMuted;

  useEffect(() => {
    if (!reel.uploadedBy) {
      setCanEdit(false);
      return;
    }
    checkCanEditReel(reel.uploadedBy).then(setCanEdit).catch(() => setCanEdit(false));
  }, [reel.uploadedBy]);

  const hasClip = reel.clipStartSeconds != null && reel.clipEndSeconds != null;

  const reelDisplay = useMemo(
    () => resolveReelDisplay({
      sourceAspectRatio: reel.sourceAspectRatio,
      cropMode: reel.cropMode,
      focalX: reel.focalX,
      focalY: reel.focalY,
    }),
    [reel.sourceAspectRatio, reel.cropMode, reel.focalX, reel.focalY]
  );

  useEffect(() => {
    if (audioUnlocked) setAutoplayFallbackMuted(false);
  }, [audioUnlocked]);

  useEffect(() => {
    if (isInView) {
      setUserMuted(false);
      setAutoplayFallbackMuted(false);
    }
  }, [isInView]);

  const toggleMute = useCallback(() => {
    const effectivelyMuted = !audioUnlocked || userMuted || autoplayFallbackMuted;
    if (effectivelyMuted) {
      setUserMuted(false);
      setAutoplayFallbackMuted(false);
      const video = videoRef.current;
      if (video && isInView) {
        video.muted = false;
        const playPromise = video.play();
        if (playPromise) {
          playPromise.catch(() => {
            setAutoplayFallbackMuted(true);
            video.muted = true;
            video.play().catch(() => {});
          });
        }
      }
    } else {
      setUserMuted(true);
    }
  }, [audioUnlocked, userMuted, autoplayFallbackMuted, isInView]);

  useEffect(() => {
    if (!cardRef.current) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsInView(entry.isIntersecting);
        if (entry.isIntersecting) onVisible?.();
      },
      { threshold: 0.6 }
    );
    observer.observe(cardRef.current);
    return () => observer.disconnect();
  }, [onVisible]);

  useEffect(() => {
    if (!isLast || !onNearEnd || !cardRef.current) return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) onNearEnd();
      },
      { threshold: 0.5 }
    );
    observer.observe(cardRef.current);
    return () => observer.disconnect();
  }, [isLast, onNearEnd]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !reel.videoUrl) return undefined;

    if (isInView) {
      video.muted = muted;
      const playPromise = video.play();
      if (playPromise) {
        playPromise.catch(() => {
          if (!muted) {
            setAutoplayFallbackMuted(true);
            video.muted = true;
            video.play().catch(() => {});
          }
        });
      }
    } else {
      video.pause();
    }
    return undefined;
  }, [isInView, reel.videoUrl, muted]);

  const handleLike = () => {
    onLike?.();
  };

  return (
    <div
      ref={cardRef}
      className="relative w-full h-full md:h-screen snap-start snap-always shrink-0 bg-black overflow-hidden group"
    >
      {reel.videoUrl ? (
        <BrandVideoPlayer
          ref={videoRef}
          src={reel.videoUrl}
          poster={reel.videoThumbnail}
          className="absolute inset-0 w-full h-full"
          videoClassName="w-full h-full"
          objectFit={reelDisplay.objectFit}
          objectPosition={reelDisplay.objectPosition}
          loop
          tapAction="togglePlay"
          showPlayPauseControl={false}
          muted={muted}
          onMutedChange={(nextMuted) => {
            setUserMuted(nextMuted);
            if (!nextMuted) setAutoplayFallbackMuted(false);
          }}
          clipStartSeconds={hasClip ? reel.clipStartSeconds : null}
          clipEndSeconds={hasClip ? reel.clipEndSeconds : null}
          centerPlaySize="sm"
        />
      ) : (
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-80"
          style={{ backgroundImage: `url(${reel.videoThumbnail})` }}
        />
      )}

      <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/90 pointer-events-none z-10" />

      <div className="absolute top-6 left-4 md:top-8 md:left-6 z-30 pointer-events-auto">
        <button
          type="button"
          onClick={toggleMute}
          className="flex flex-col items-center gap-1.5 group/btn"
          aria-label={muted ? 'Silenciado' : 'Silenciar'}
        >
          <div
            className={`p-3.5 rounded-full backdrop-blur-md transition-all ${
              muted
                ? 'bg-white/10 group-hover/btn:bg-white/20'
                : 'bg-brand-cyan/25 border border-brand-cyan/40'
            }`}
          >
            <SafeIcon
              name={muted ? 'VolumeX' : 'Volume2'}
              className={`w-6 h-6 ${muted ? 'text-white' : 'text-brand-cyan'}`}
            />
          </div>
        </button>
      </div>

      <div className="absolute inset-0 flex flex-col justify-end p-6 md:p-12 pb-10 md:pb-16 pointer-events-none z-20">
        <div className="absolute top-6 left-[4.5rem] md:top-8 md:left-20 flex gap-2 items-center flex-wrap max-w-[calc(100%-6rem)]">
          {(reel.tags ?? []).map((tag) => (
            <span key={tag} className="px-3 py-1 bg-white/10 backdrop-blur-md rounded-full text-[10px] font-bold text-white border border-white/20 uppercase tracking-widest">
              {tag}
            </span>
          ))}
          {canEdit && (
            <button
              type="button"
              onClick={() => navigate(`/app/reels/${reel.id}/editar`)}
              className="pointer-events-auto p-2 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-white hover:bg-white/20 transition-colors"
              aria-label="Editar reel"
              title="Editar reel"
            >
              <SafeIcon name="Pencil" className="w-4 h-4" />
            </button>
          )}
        </div>

        <div className="flex items-end justify-between w-full pointer-events-auto">
          <div className="flex-1 pr-12">
            <h2 className="text-2xl md:text-5xl font-black text-white mb-3 leading-[1.1] drop-shadow-2xl italic tracking-tight uppercase">
              {reel.title}
            </h2>
            <div className="flex items-center gap-2 mb-6">
              <div className="w-8 h-8 rounded-full gfi-gradient-bg p-[1px]">
                <div className="w-full h-full rounded-full bg-white flex items-center justify-center text-[10px] font-black text-brand-navy">
                  {reel.speaker.charAt(0)}
                </div>
              </div>
              <p className="text-sm font-bold text-white/90 uppercase tracking-wider">{reel.speaker}</p>
            </div>

            {reel.fullSessionId && (
              <button
                type="button"
                onClick={() => navigate(`/app/entrenamientos/${reel.fullSessionId}`)}
                className="group flex items-center gap-3 gfi-gradient-bg text-brand-navy px-6 py-3 rounded-full font-black text-xs uppercase tracking-widest transition-all transform hover:scale-105 active:scale-95 shadow-xl"
              >
                <SafeIcon name="Play" className="w-4 h-4 fill-current" />
                Ver Sesión Completa
              </button>
            )}
          </div>

          <div className="flex flex-col items-center gap-6 pb-2">
            <button type="button" onClick={handleLike} className="flex flex-col items-center gap-1.5 group/btn">
              <div className={`p-3.5 rounded-full backdrop-blur-md transition-all ${isLiked ? 'bg-red-500' : 'bg-white/10 group-hover/btn:bg-white/20'}`}>
                <SafeIcon name="Heart" className={`w-6 h-6 transition-colors ${isLiked ? 'text-white fill-current' : 'text-white'}`} />
              </div>
              <span className="text-[10px] font-black text-white uppercase tracking-tighter">{reel.likes}</span>
            </button>

            <button type="button" className="flex flex-col items-center gap-1.5 group/btn">
              <div className="p-3.5 rounded-full bg-white/10 backdrop-blur-md group-hover/btn:bg-white/20 transition-all">
                <SafeIcon name="MessageCircle" className="w-6 h-6 text-white" />
              </div>
              <span className="text-[10px] font-black text-white uppercase tracking-tighter">{reel.comments}</span>
            </button>

            <button type="button" className="flex flex-col items-center gap-1.5 group/btn">
              <div className="p-3.5 rounded-full bg-white/10 backdrop-blur-md group-hover/btn:bg-white/20 transition-all">
                <SafeIcon name="Share2" className="w-6 h-6 text-white" />
              </div>
              <span className="text-[10px] font-black text-white uppercase tracking-tighter">GFI</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
